import { useState } from 'react';

import TagManager from 'react-gtm-module';

import { Language } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$types';
import { useDidMount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { LoggerHelper } from '@helpers';
import { useAlert, useCookiesConsent, useModal, useTranslation } from '@hooks';
import { useFetchAccountDeleteCookies } from '@services';
import { useBaseStore, useCookiesStore, useSearchParamsStore, useServicesStore } from '@store';

import { UpdateCredentialsScreenProps } from './updateCredentials';

export const useUpdateCredentialsHelper = (): UpdateCredentialsScreenProps => {
	const { fetchContents } = useTranslation();
	const { initCookiesConsentModal } = useCookiesConsent();
	const genericErrorModal = useModal('genericError');
	const invalidDataAlert = useAlert('invalidData');

	const language = useSearchParamsStore((state) => state.language);
	const cookieResponseType = useCookiesStore((state) => state.responseType);
	const updateCredentialType = useServicesStore((state) => state.accountChallenge?.updateCredentialType);
	const screenGoto = useBaseStore((state) => state.goto);

	const { fetch: fetchAccountDeleteCookies } = useFetchAccountDeleteCookies();

	const [isLoading, setIsLoading] = useState(true);

	useDidMount(() => {
		handleInit();
	});

	const handleInit = async () => {
		try {
			// get both generic and client translations
			const translations = await fetchContents(language || Language.pt);

			let showCookiesConsent = translations.showCookiesConsentA;
			let gtmActive = translations['configs.gtm.activeA'];

			if (window.location.pathname.includes('/b/')) {
				showCookiesConsent = translations.showCookiesConsentB;
				gtmActive = translations['configs.gtm.activeB'];
			}

			// manage GTM script injection depending on configuration
			const gtmContainerId = translations['configs.gtm.containerId'];
			if (!!gtmContainerId && gtmActive === 'true') {
				TagManager.initialize({
					gtmId: gtmContainerId,
				});
			}

			// manage cookies modal visibility and cookie response from BE
			await Promise.all([initCookiesConsentModal(showCookiesConsent), handleCookieResponse()]);
		} catch {
			screenGoto('error');
		} finally {
			setIsLoading(false);
		}
	};

	const handleCookieResponse = async () => {
		if (cookieResponseType) {
			/* clear the cookies stored in browser via service */
			await fetchAccountDeleteCookies({});

			/* Process based on the cookie response type */
			switch (cookieResponseType) {
				case 'updateCredentials':
					/* Do nothing, default state */
					break;
				case 'serviceUnavailable':
					/* Show a generic error modal for service unavailable */
					genericErrorModal.show();
					break;
				case 'invalidUpdateCredentials':
					/* Show an invalid data alert */
					invalidDataAlert.show();
					break;
				default:
					/* Handle unexpected cookie response types */
					LoggerHelper.log(
						'App',
						'UpdateCredentials > handleCookieResponse > cookieResponseType not handled:',
						cookieResponseType,
						{ overlay: { show: true } }
					);

					return Promise.reject();
			}
		}
	};

	return {
		isLoading,
		updateCredentialType,
	};
};
